import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, AlertTriangle, Check, MapPin, Clock } from 'lucide-react';
import { TeamAApi, A_CATEGORIES, ApiError, type PublishItemResult, type APrivacyStatus } from '@ai-tiaozs/shared';
import { ImageUploader } from '@/components/ImageUploader';
import { VoiceInput } from '@/components/VoiceInput';

/**
 * A 队 · ① 发布失物
 * 信任型自助领取:
 *  1) 拍照 + 填 AI 无法判断的地点/时间/存放点
 *  2) AI 提取公开特征,学生确认后发布到公开失物墙
 */
type Phase = 'photo' | 'ai_confirm';

export function TeamAPublish() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('photo');
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [category, setCategory] = useState<(typeof A_CATEGORIES)[number]>('水杯');
  const [foundLocation, setFoundLocation] = useState('');
  const [foundTime, setFoundTime] = useState('');
  const [storageLocation, setStorageLocation] = useState('');
  const [aiFeatures, setAiFeatures] = useState<PublishItemResult['features'] | null>(null);
  const [privacyCheck, setPrivacyCheck] = useState<{ status: APrivacyStatus; warning: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busyRef = useRef(false);

  const featureValues = () => aiFeatures ? [
    aiFeatures.color, aiFeatures.shape, aiFeatures.pattern,
    aiFeatures.text_mark, aiFeatures.size,
  ].map((v) => v?.trim()).filter(Boolean) as string[] : [];

  const generatedTitle = () => {
    const color = aiFeatures?.color?.trim();
    if (color && color !== '未知') return `${color}${category}`.slice(0, 40);
    return category;
  };

  const generatedDescription = () => {
    const parts = [
      ...featureValues(),
      ...(aiFeatures?.notes ?? []).map((n) => n.trim()).filter(Boolean),
      foundTime.trim() ? `捡到时间:${foundTime.trim()}` : '',
    ];
    const unique = [...new Set(parts)].filter((v) => v !== '未知');
    return (unique.length
      ? unique.join('；')
      : 'AI 未能识别清晰特征，请根据照片、类别和捡到地点核对。'
    ).slice(0, 200);
  };

  const extractFeatures = async () => {
    if (busyRef.current) return;
    if (!photoDataUrl) { setError('请先上传物品照片'); return; }
    if (!foundLocation.trim()) { setError('请填写捡到地点,AI 无法从照片判断地点'); return; }
    if (!storageLocation.trim()) { setError('请填写固定存放地点,失主需要知道去哪里领取'); return; }
    busyRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const r = await TeamAApi.analyzePublish({
        photoDataUrl,
        category,
        foundLocation: foundLocation.trim(),
        foundTime: foundTime.trim(),
      });
      setAiFeatures(r.features);
      setPrivacyCheck({ status: r.privacyCheckStatus, warning: r.privacyWarning });
      setPhase('ai_confirm');
    } catch (e) {
      const err = e as ApiError & { data?: PublishItemResult };
      if (err.code === 'PRIVACY_BLOCKED' && err.data) {
        setAiFeatures(err.data.features);
        setPrivacyCheck({ status: 'blocked', warning: err.data.privacyWarning });
        setPhase('ai_confirm');
      } else {
        setError(e instanceof ApiError ? e.message : 'AI 识别失败');
      }
    } finally {
      busyRef.current = false;
      setLoading(false);
    }
  };

  const submit = async () => {
    if (busyRef.current) return;
    if (!photoDataUrl) { setError('请先上传物品照片'); return; }
    if (!foundLocation.trim()) { setError('请填写捡到地点'); return; }
    if (!storageLocation.trim()) { setError('请填写固定存放地点'); return; }
    busyRef.current = true;
    setLoading(true);
    setError(null);
    try {
      await TeamAApi.publish({
        photoDataUrl,
        category,
        title: generatedTitle(),
        description: generatedDescription(),
        foundLocation: foundLocation.trim(),
        storageLocation: storageLocation.trim(),
        aiFeaturesConfirmed: featureValues(),
      });
      navigate('/');
    } catch (e) {
      const err = e as ApiError & { data?: PublishItemResult };
      if (err.code === 'PRIVACY_BLOCKED' && err.data) {
        setPrivacyCheck({ status: 'blocked', warning: err.data.privacyWarning });
        setError('仍被隐私检查拦截,请遮挡后重新上传');
        setPhase('ai_confirm');
      } else {
        setError(e instanceof ApiError ? e.message : '发布失败');
      }
    } finally {
      busyRef.current = false;
      setLoading(false);
    }
  };

  const privacyBlocked = privacyCheck?.status === 'blocked';

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-bold">① 发布失物</h1>
        <p className="text-xs text-slate-500">拍照 → 填地点和存放点 → AI 提取特征 → 确认发布</p>
      </header>

      {phase === 'photo' && (
        <section className="card space-y-3">
          <p className="text-xs text-slate-500">第一步:拍照,并填写 AI 无法从照片判断的信息</p>
          <div>
            <label className="text-xs text-slate-500">物品照片(请先遮挡姓名/班级/证件号)</label>
            <div className="mt-1">
              <ImageUploader label="拍照 / 上传" onChange={setPhotoDataUrl} />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">类别</label>
            <select
              className="input mt-1"
              value={category}
              onChange={(e) => setCategory(e.target.value as (typeof A_CATEGORIES)[number])}
            >
              {A_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500">
                <MapPin className="mr-1 inline h-3 w-3" />
                捡到地点(必填)
              </label>
              <div className="mt-1">
                <VoiceInput
                  value={foundLocation}
                  onChange={setFoundLocation}
                  placeholder="操场跑道"
                  maxLength={40}
                  ariaLabel="捡到地点"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500">
                <Clock className="mr-1 inline h-3 w-3" />
                捡到时间(可选)
              </label>
              <div className="mt-1">
                <VoiceInput
                  value={foundTime}
                  onChange={setFoundTime}
                  placeholder="今天上午第三节课"
                  maxLength={40}
                  ariaLabel="捡到时间"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="flex items-center gap-1 text-xs font-semibold text-teamA-primary">
              <MapPin className="h-3 w-3" /> 固定存放地点(失主领取处)
            </label>
            <div className="mt-1">
              <VoiceInput
                value={storageLocation}
                onChange={setStorageLocation}
                placeholder="如:老师办公室 / 体育器材室 / 失物招领处"
                maxLength={40}
                ariaLabel="固定存放地点"
              />
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              失主会看到这个地点并自行前往核对领取
            </p>
          </div>
          <p className="rounded-md bg-teamA-primary/5 px-3 py-2 text-[11px] text-slate-600">
            AI 只识别照片里的外观特征,捡到地点、时间和存放地点需要发布人填写。
          </p>
          {error && <p className="text-xs text-teamA-danger">{error}</p>}
          <button
            onClick={extractFeatures}
            disabled={loading || !photoDataUrl || !foundLocation.trim() || !storageLocation.trim()}
            className="btn-primary w-full"
          >
            {loading ? 'AI 识别中…' : (
              <><Sparkles className="mr-1 h-4 w-4" /> AI 识别物品特征</>
            )}
          </button>
        </section>
      )}

      {phase === 'ai_confirm' && aiFeatures && (
        <section className="card space-y-3">
          <h2 className="flex items-center gap-1 text-sm font-semibold text-teamA-primary">
            <Sparkles className="h-4 w-4" /> AI 识别结果(可修改)
          </h2>
          {privacyCheck && privacyCheck.status !== 'safe' && (
            <div className={`rounded-md px-3 py-2 text-xs ${
              privacyBlocked
                ? 'bg-teamA-danger/10 text-teamA-danger'
                : 'bg-teamA-warning/10 text-teamA-warning'
            }`}>
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              {privacyBlocked ? '🚫 隐私检查未通过' : '⚠️ 隐私提示'}:
              {privacyCheck.warning || '请确认是否包含敏感信息'}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <label className="text-slate-500">主色</label>
              <input
                className="input mt-1"
                value={aiFeatures.color ?? ''}
                onChange={(e) => setAiFeatures({ ...aiFeatures, color: e.target.value })}
              />
            </div>
            <div>
              <label className="text-slate-500">形状</label>
              <input
                className="input mt-1"
                value={aiFeatures.shape ?? ''}
                onChange={(e) => setAiFeatures({ ...aiFeatures, shape: e.target.value })}
              />
            </div>
            <div>
              <label className="text-slate-500">文字标记</label>
              <input
                className="input mt-1"
                value={aiFeatures.text_mark ?? ''}
                onChange={(e) => setAiFeatures({ ...aiFeatures, text_mark: e.target.value })}
              />
            </div>
            <div>
              <label className="text-slate-500">图案</label>
              <input
                className="input mt-1"
                value={aiFeatures.pattern ?? ''}
                onChange={(e) => setAiFeatures({ ...aiFeatures, pattern: e.target.value })}
              />
            </div>
            <div>
              <label className="text-slate-500">相对大小</label>
              <input
                className="input mt-1"
                value={aiFeatures.size ?? ''}
                onChange={(e) => setAiFeatures({ ...aiFeatures, size: e.target.value })}
              />
            </div>
          </div>
          {aiFeatures.notes && aiFeatures.notes.length > 0 && (
            <div>
              <p className="text-xs text-slate-500">其它可见特征:</p>
              <ul className="mt-1 list-inside list-disc text-xs text-slate-700">
                {aiFeatures.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}

          <div className="rounded-md bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
            <p>
              <MapPin className="mr-1 inline h-3 w-3" />
              捡到地点:<span className="ml-1 font-medium text-slate-700">{foundLocation || '未填'}</span>
            </p>
            <p className="mt-1">
              <Clock className="mr-1 inline h-3 w-3" />
              捡到时间:<span className="ml-1 font-medium text-slate-700">{foundTime || '未填'}</span>
            </p>
            <p className="mt-1">
              <MapPin className="mr-1 inline h-3 w-3" />
              固定存放:<span className="ml-1 font-medium text-slate-700">{storageLocation || '未填'}</span>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setPhase('photo')} className="btn-outline">
              重新拍照
            </button>
            <button
              onClick={submit}
              disabled={loading || privacyBlocked}
              className="btn-primary"
            >
              {loading ? '发布中…' : (
                <><Check className="mr-1 h-4 w-4" /> 确认并发布</>
              )}
            </button>
          </div>
          {privacyBlocked && (
            <p className="text-[11px] text-teamA-danger">
              隐私检查未通过,请遮挡后重新上传
            </p>
          )}
          {error && <p className="text-xs text-teamA-danger">{error}</p>}
        </section>
      )}

    </div>
  );
}
